import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { uploadFileToActiveStorage, DirectUploadResult } from "helpers/activeStorage";
import { AttachmentRow, MAX_ATTACHMENTS_TOTAL_BYTES, isAllowedAttachment } from "features/Resources/Shared/attachments";

let seq = 0;
const nextKey = () => `up-${(seq += 1)}-${Date.now()}`;
const MAX_MB = MAX_ATTACHMENTS_TOTAL_BYTES / (1024 * 1024);

type Options = {
  /** Bytes already committed (persisted or staged), counted toward the limit. */
  baseBytes?: number;
  /** Called once per file when its direct upload completes. */
  onUploaded: (result: DirectUploadResult) => void;
};

// Size-validates + direct-uploads files, tracks in-flight rows, and hands off
// each signed id on completion. Caller decides what to do with it (stage/persist).
export function useFileUploads({ baseBytes = 0, onUploaded }: Options) {
  const toast = useToast();
  const [rows, setRows] = useState<AttachmentRow[]>([]);

  // Avoid stale closures: a completing upload should call the latest handler.
  const onUploadedRef = useRef(onUploaded);
  useEffect(() => {
    onUploadedRef.current = onUploaded;
  }, [onUploaded]);

  const add = useCallback(
    (files: FileList | File[]) => {
      setRows((prev) => {
        let running = baseBytes + prev.filter((r) => r.status === "uploading").reduce((sum, r) => sum + r.byteSize, 0);
        const started: AttachmentRow[] = [];

        Array.from(files).forEach((file) => {
          if (!isAllowedAttachment(file)) {
            toast({
              title: "File type not allowed",
              description: `"${file.name}" can't be attached. Allowed: documents (PDF, Word, Excel, PowerPoint), images, and text files.`,
              status: "error",
              duration: 6000,
            });
            return;
          }
          if (running + file.size > MAX_ATTACHMENTS_TOTAL_BYTES) {
            toast({
              title: "Attachment too large",
              description: `"${file.name}" would put attachments over the ${MAX_MB} MB limit. It wasn't added.`,
              status: "error",
              duration: 6000,
            });
            return;
          }
          running += file.size;
          const key = nextKey();
          started.push({ key, filename: file.name, byteSize: file.size, status: "uploading", progress: 0 });

          uploadFileToActiveStorage(file, (percent) => {
            setRows((cur) => cur.map((r) => (r.key === key ? { ...r, progress: percent } : r)));
          })
            .then((result) => {
              setRows((cur) => cur.filter((r) => r.key !== key)); // hand off to the caller's list
              onUploadedRef.current(result);
            })
            .catch(() => {
              setRows((cur) => cur.map((r) => (r.key === key ? { ...r, status: "error", error: "Upload failed" } : r)));
              toast({ title: "Upload failed", description: `Could not upload "${file.name}".`, status: "error" });
            });
        });

        return [...prev, ...started];
      });
    },
    [baseBytes, toast],
  );

  const cancel = useCallback((key: string) => setRows((prev) => prev.filter((r) => r.key !== key)), []);
  const isUploading = useMemo(() => rows.some((r) => r.status === "uploading"), [rows]);

  return { inFlight: rows, add, cancel, isUploading };
}
